import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Train, Map, Stamp, LayoutDashboard, LogOut, User, Compass, Users, ShoppingBag, GraduationCap, Menu, Heart, Activity } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import MobileNavDrawer from "./MobileNavDrawer";

const NAV_TRAVELLER = [
  { to: "/routes", label: "Tours", icon: Compass },
  { to: "/plan-trip", label: "Plan", icon: Train },
  { to: "/map", label: "Atlas", icon: Map },
  { to: "/groups", label: "Groups", icon: Users },
  { to: "/schools", label: "Schools", icon: GraduationCap },
  { to: "/saved", label: "Saved", icon: Heart },
  { to: "/passport", label: "Passport", icon: Stamp },
];

// Admin: a stripped-down nav. The conductor doesn't need to plan their own
// trips or collect passport stamps — they need their desk, the map for
// visual context, and group inquiries. Everything else is hidden.
const NAV_ADMIN = [
  { to: "/admin", label: "Desk", icon: Activity },
  { to: "/admin/logs", label: "Logs", icon: LayoutDashboard },
  { to: "/map", label: "Atlas", icon: Map },
  { to: "/groups", label: "Groups", icon: Users },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.email === "admin@interquest.app";
  const navItems = isAdmin ? NAV_ADMIN : NAV_TRAVELLER;

  return (
    <header
      className="fixed inset-x-0 z-40 bg-ink border-b border-panel-border"
      style={{ top: "env(safe-area-inset-top, 0px)" }}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 md:px-6 py-3">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
          <span className="inline-flex h-9 w-9 items-center justify-center bg-gold text-ink rounded-sm group-hover:bg-cream transition-colors">
            <Train className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="font-display text-xl md:text-2xl tracking-[0.18em] text-cream group-hover:text-gold transition-colors">
            INTER<span className="text-gold group-hover:text-cream">QUEST</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-4 py-2 rounded-sm font-display text-sm tracking-[0.18em] uppercase transition-colors ${
                  isActive ? "text-gold bg-panel border border-panel-border" : "text-cream-dim hover:text-cream"
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <Link to="/cart" data-testid="nav-cart" className="relative hidden sm:inline-flex items-center text-cream-dim hover:text-gold transition-colors p-2 rounded-sm">
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-gold text-ink text-[10px] font-display tracking-wider rounded-full h-5 min-w-5 px-1 inline-flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>
          {user ? (
            <>
              <div
                data-testid="xp-pill"
                className="hidden sm:flex items-center gap-2 bg-panel border border-gold/50 rounded-full px-3 py-1 text-gold text-xs font-body font-semibold"
              >
                <span className="font-display tracking-widest text-sm">LVL {user.level}</span>
                <span className="h-1 w-1 rounded-full bg-gold" />
                <span>{user.xp} XP</span>
              </div>
              <button
                data-testid="logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                className="hidden md:inline-flex iq-btn-ghost"
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden md:inline text-sm">Sign out</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden md:inline-flex iq-btn-ghost text-sm">
                <User className="h-4 w-4" /> Sign in
              </Link>
              <Link to="/register" data-testid="nav-register" className="hidden md:inline-flex iq-btn-secondary text-sm py-2">
                Join
              </Link>
            </>
          )}

          {/* Mobile hamburger */}
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            className="md:hidden h-11 w-11 inline-flex items-center justify-center rounded-sm text-cream hover:text-gold hover:bg-panel-border/40 transition-colors"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <MobileNavDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={user}
        onLogout={() => { logout(); navigate("/"); }}
        cartCount={count}
      />
    </header>
  );
}
